import { InputAdornment, MenuItem, SelectChangeEvent, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { FormSelect } from "component/FormSelect";
import { LayerIcon } from "component/Icons";
import { UNKNOWN } from "constant";
import orderBy from "lodash/orderBy";
import { AssetIdentifier, AssetStatus } from "module/common";
import { useMemo } from "react";
import { COLORS } from "style";
import { transformAssetView } from "utility/transformer";
import * as api from "api";
import { RecommendationWithRelated } from "type/model/api";
import { AssetView, AssetViewStatus } from "type/model/view";
import { LoadingIndicator } from "component/LoadingIndicator";
import { ErrorAlert } from "component/ErrorAlert";

interface Props {
  label: string;
  value: string | number | null | undefined;
  onChange?: (event: SelectChangeEvent<unknown>, child: React.ReactNode) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error?: string | undefined;
  changeAssetInfo?: (id: number, asset_name: string | null | undefined, callSign: string | null | undefined) => void;
  recommendations?: RecommendationWithRelated[];
}

const FIGURES: { label: string; key: keyof RecommendationWithRelated }[] = [
  { label: "Time to patient", key: "time_to_patient" },
  { label: "Time out of hospital", key: "time_out_of_hospital" },
  { label: "Total medical time", key: "total_medical_time" },
  { label: "Total mission time", key: "total_mission_time" },
];

const RecommendationFigures = ({ recommendation }: { recommendation: RecommendationWithRelated }) => (
  <Stack sx={{ whiteSpace: "nowrap", px: 2 }}>
    {FIGURES.map(({ label, key }) => {
      const minutes = (recommendation[key] as number | undefined) ?? 0;

      return (
        <Typography key={key} variant="body2" color={COLORS.DARK_SOULS}>
          {label} {Math.floor(minutes / 60)}hr {minutes % 60}min
        </Typography>
      );
    })}
  </Stack>
);

export const AssetSelect = (props: Props) => {
  const { recommendations } = props;
  const assetsQuery = useQuery({ queryKey: ["assets"], queryFn: api.fetchAssets });
  const { assets, byAssetId } = useMemo(() => {
    const byAssetId = new Map((recommendations ?? []).map((recommendation) => [recommendation.asset_id, recommendation]));
    const all = (assetsQuery.data?.data ?? []).map(transformAssetView);
    const dashboardOrder = (asset: AssetView) =>
      `${asset.status === AssetViewStatus.Online ? 0 : 1}  ${asset.dashboardOrder.toString(10).padStart(6)}`;

    if (!recommendations) {
      return { assets: orderBy(all, dashboardOrder), byAssetId };
    }

    const recommended = orderBy(
      all.filter((asset) => byAssetId.has(asset.id)),
      (asset) => byAssetId.get(asset.id)?.position ?? Infinity
    );
    const rest = orderBy(
      all.filter((asset) => !byAssetId.has(asset.id)),
      dashboardOrder
    );

    return { assets: [...recommended, ...rest], byAssetId };
  }, [assetsQuery.data, recommendations]);

  const getAssetName = (assetId: unknown) => {
    if (!assetId) {
      return (
        <Typography variant="body2" color={COLORS.DARK_SOULS}>
          Select an asset for this mission
        </Typography>
      );
    }
    const result = assets.find((asset) => asset.id === assetId);
    return result?.name ?? "";
  };

  if (assetsQuery.isPending) {
    return <LoadingIndicator size={20} />;
  }

  if (assetsQuery.isError) {
    return <ErrorAlert>Something went wrong!</ErrorAlert>;
  }

  const { changeAssetInfo, onChange, recommendations: _recommendations, ...otherProps } = props;

  return (
    <FormSelect
      {...otherProps}
      required
      fullWidth
      displayEmpty={true}
      renderValue={getAssetName}
      onChange={(e, child) => {
        onChange && onChange(e, child);
      }}
      startAdornment={
        <InputAdornment position="start">
          <LayerIcon />
        </InputAdornment>
      }
      items={assets.map((asset) => ({
        value: asset.id.toString(),
        element: (
          <MenuItem key={asset.id} value={asset.id} sx={{ justifyContent: "space-between" }} autoFocus={false}>
            <AssetIdentifier asset={asset} />
            {byAssetId.get(asset.id) ? <RecommendationFigures recommendation={byAssetId.get(asset.id)!} /> : null}
            <AssetStatus asset={asset} />
          </MenuItem>
        ),
        readOnlyText: asset.name || UNKNOWN,
      }))}
    />
  );
};
